import * as zod from 'zod';

/**
 * Per-robot compliance controller tunables, stored as a JSONB blob on the
 * `compliance_settings.data` column. Replaces the file-backed
 * `complianceController` service, which merged a `defaults.json` over
 * `/etc/standardbots/configuration/compliance_controller.json`.
 *
 * Keys are deliberately snake_case rather than the usual TS camelCase: each
 * one must match a `generate_parameter_library` parameter name in
 * `oscbf_ros_node_parameters.yaml` and a field on the
 * `compliancecontrolbot.ComplianceSettings` proto. Keeping all three
 * identical removes the name-mapping layer that would otherwise sit on both
 * sides of the wire, where a single typo silently drops a tunable.
 *
 * 71 of these are ROS parameters applied straight onto the node's parameter
 * object. The remaining four — `enable_debug_topics`, `debug_publish_rate`,
 * `dt`, `enable_payload_estimation` — are node-level settings the ROS node
 * handles specially rather than as parameters.
 */

const Vec3 = zod.tuple([zod.number(), zod.number(), zod.number()]);

const Vec6 = zod.tuple([
  zod.number(),
  zod.number(),
  zod.number(),
  zod.number(),
  zod.number(),
  zod.number(),
]);

export const ComplianceSettingsConfigBase = zod.object({
  // --- Task-space impedance gains ---
  kp_pos: zod.number(),
  kd_pos: zod.number(),
  ki_pos: zod.number(),
  kp_rot: zod.number(),
  kd_rot: zod.number(),
  ki_rot: zod.number(),
  kp_pos_vel: zod.number(),
  ki_pos_vel: zod.number(),
  kp_rot_vel: zod.number(),
  ki_rot_vel: zod.number(),
  kp_joint: zod.number(),
  kd_joint: zod.number(),

  // --- Error clipping ---
  pos_error_clip: zod.number(),
  rot_error_clip: zod.number(),
  vel_error_clip: zod.number(),
  omega_error_clip: zod.number(),
  integral_pos_error_clip: zod.number(),
  integral_rot_error_clip: zod.number(),

  // --- End-effector payload (physically measured per arm) ---
  mass_ee: zod.number(),
  cog_ee_in_sensor: Vec3,

  // --- Force/torque sensing ---
  force_torque_dead_zone: Vec6,
  calibration_offset: Vec6,
  wrench_alpha: zod.number(),

  // --- Workspace bounds, robot base frame, meters ---
  /** End-effector containment box (`enable_stage*_ee_set_containment_cbf`). */
  ee_pos_min: Vec3,
  ee_pos_max: Vec3,
  /** Whole-body box — applies to every robot collision point. */
  wb_pos_min: Vec3,
  wb_pos_max: Vec3,

  // --- External collision sphere ---
  collision_pos: Vec3,
  collision_radii: zod.number(),

  // --- Joint limits ---
  max_joint_torques: Vec6,
  max_joint_velocities: Vec6,
  max_joint_accelerations: Vec6,
  tau_rate: Vec6,
  joint_limits_margin_deg: zod.number(),
  joint_limits_alpha_multiplier: zod.number(),

  // --- Force limiting ---
  max_force: Vec3,
  force_limit_alpha: zod.number(),
  force_limit_recovery: zod.number(),

  // --- Singularity handling ---
  singularity_tol: zod.number(),
  singularity_alpha_multiplier: zod.number(),
  damping_near_singularities: zod.boolean(),

  // --- CBF solver ---
  alpha_gain: zod.number(),
  alpha_2_gain: zod.number(),
  alpha_relu: zod.boolean(),
  relax_cbf: zod.boolean(),
  qp_rot_obj_weight: zod.number(),

  // --- Stage-1 (velocity QP) barrier enables ---
  enable_stage1_cbf: zod.boolean(),
  enable_stage1_singularity_cbf: zod.boolean(),
  enable_stage1_ee_set_containment_cbf: zod.boolean(),
  enable_stage1_joint_limits_cbf: zod.boolean(),
  enable_stage1_collision_cbf: zod.boolean(),
  enable_stage1_whole_body_cbf: zod.boolean(),
  enable_stage1_self_collision_cbf: zod.boolean(),
  enable_stage1_velocity_limits: zod.boolean(),
  enable_stage1_force_limit_cbf: zod.boolean(),

  // --- Stage-2 (torque QP) barrier enables ---
  enable_stage2_cbf: zod.boolean(),
  enable_stage2_singularity_cbf: zod.boolean(),
  enable_stage2_ee_set_containment_cbf: zod.boolean(),
  enable_stage2_joint_limits_cbf: zod.boolean(),
  enable_stage2_collision_cbf: zod.boolean(),
  enable_stage2_whole_body_cbf: zod.boolean(),
  enable_stage2_self_collision_cbf: zod.boolean(),
  enable_stage2_velocity_limits: zod.boolean(),
  enable_stage2_acceleration_limits: zod.boolean(),
  enable_stage2_force_limit_cbf: zod.boolean(),

  // --- Control mode ---
  use_effort_mode: zod.boolean(),
  use_commanded_pose_for_error: zod.boolean(),

  // --- Simulation ---
  sim: zod.boolean(),
  override_sim: zod.boolean(),

  // --- Node-level settings (not ROS parameters) ---
  /** Control loop rate in Hz; the node stores it as `dt = 1 / value`. */
  dt: zod.number().positive(),
  enable_debug_topics: zod.boolean(),
  debug_publish_rate: zod.number().positive(),
  enable_payload_estimation: zod.boolean(),

  // --- Debug print flags (ROS parameters) ---
  print_tool_compensated_forces: zod.boolean(),
  print_execution_time: zod.boolean(),
});

const AXIS_LABELS = ['x', 'y', 'z'] as const;

/**
 * Both workspace boxes must be well-formed on every axis. A box with an
 * inverted axis makes its containment barrier unsatisfiable, which the QP
 * resolves by relaxing the constraint — the robot silently loses the bound
 * rather than refusing to move, so this has to be caught at the write
 * boundary.
 */
function checkBoundsOrdering(
  data: Partial<zod.infer<typeof ComplianceSettingsConfigBase>>,
  ctx: zod.RefinementCtx,
): void {
  const boxes = [
    { label: 'EE', minKey: 'ee_pos_min', maxKey: 'ee_pos_max' },
    { label: 'whole-body', minKey: 'wb_pos_min', maxKey: 'wb_pos_max' },
  ] as const;

  for (const { label, minKey, maxKey } of boxes) {
    const min = data[minKey];
    const max = data[maxKey];

    // A partial patch may carry only one half of a box; the merged row is
    // re-validated in full, so skip rather than reject here.
    if (!min || !max) continue;

    for (let axis = 0; axis < AXIS_LABELS.length; axis += 1) {
      if (min[axis] >= max[axis]) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: [minKey, axis],
          message: `${label} bounds: ${minKey}.${AXIS_LABELS[axis]} must be strictly less than ${maxKey}.${AXIS_LABELS[axis]}`,
        });
      }
    }
  }
}

export const ComplianceSettingsConfig =
  ComplianceSettingsConfigBase.superRefine(checkBoundsOrdering);

export type ComplianceSettingsConfig = zod.infer<
  typeof ComplianceSettingsConfig
>;

/**
 * Patch schema for partial updates: every field optional, but a box supplied
 * in full still has to be well-formed. Used by the messenger
 * `SetComplianceConfig` handler and the settings UI.
 */
export const ComplianceSettingsConfigPatch =
  ComplianceSettingsConfigBase.partial().superRefine(checkBoundsOrdering);

export type ComplianceSettingsConfigPatch = zod.infer<
  typeof ComplianceSettingsConfigPatch
>;

export const ComplianceSettingsData = zod.object({
  id: zod.string(),
  robotID: zod.string(),
  data: ComplianceSettingsConfig,
});

export type ComplianceSettingsData = zod.infer<typeof ComplianceSettingsData>;

/** Every tunable name, for cross-layer contract assertions. */
export const COMPLIANCE_SETTINGS_KEYS =
  ComplianceSettingsConfigBase.keyof().options;

/**
 * The four keys the ROS node handles as node-level settings rather than
 * `generate_parameter_library` parameters. Everything else in
 * {@link COMPLIANCE_SETTINGS_KEYS} must exist as a parameter in
 * `oscbf_ros_node_parameters.yaml`.
 */
export const COMPLIANCE_NODE_LEVEL_KEYS = [
  'dt',
  'enable_debug_topics',
  'debug_publish_rate',
  'enable_payload_estimation',
] as const;

export const COMPLIANCE_SETTINGS_DEFAULTS: Omit<
  ComplianceSettingsData,
  'id' | 'robotID'
> = {
  data: {
    kp_pos: 160.0,
    kd_pos: 42.0,
    ki_pos: 0.0065,
    kp_rot: 150.0,
    kd_rot: 42.0,
    ki_rot: 0.0065,
    kp_pos_vel: 4.0,
    ki_pos_vel: 0.0,
    kp_rot_vel: 3.15,
    ki_rot_vel: 0.0,
    kp_joint: 0.0,
    kd_joint: 0.0,

    pos_error_clip: 0.15,
    rot_error_clip: 0.4,
    vel_error_clip: 0.15,
    omega_error_clip: 0.8,
    integral_pos_error_clip: 0.01,
    integral_rot_error_clip: 0.5,

    mass_ee: 1.67,
    cog_ee_in_sensor: [0.0, 0.0, 0.081],

    force_torque_dead_zone: [3.5, 3.5, 3.5, 3.5, 3.5, 3.5],
    calibration_offset: [0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
    wrench_alpha: 0.65,

    ee_pos_min: [-1.35, -1.35, -0.25],
    ee_pos_max: [1.95, 1.45, 1.05],
    wb_pos_min: [-1.5, -1.5, -1.0],
    wb_pos_max: [1.5, 1.5, 1.0],

    collision_pos: [0.5, 5.5, 0.5],
    collision_radii: 0.3,

    max_joint_torques: [0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
    max_joint_velocities: [1.5, 1.5, 2.2, 3.1, 3.1, 1.7],
    max_joint_accelerations: [4.0, 4.0, 4.0, 4.0, 4.0, 4.0],
    tau_rate: [900.0, 500.0, 500.0, 500.0, 500.0, 500.0],
    joint_limits_margin_deg: 5.0,
    joint_limits_alpha_multiplier: 100.0,

    max_force: [25.0, 25.0, 25.0],
    force_limit_alpha: 0.01,
    force_limit_recovery: 0.001,

    singularity_tol: 0.1,
    singularity_alpha_multiplier: 100.0,
    damping_near_singularities: false,

    alpha_gain: 10.0,
    alpha_2_gain: 10.0,
    alpha_relu: false,
    relax_cbf: true,
    qp_rot_obj_weight: 0.05,

    enable_stage1_cbf: true,
    enable_stage1_singularity_cbf: false,
    enable_stage1_ee_set_containment_cbf: true,
    enable_stage1_joint_limits_cbf: false,
    enable_stage1_collision_cbf: false,
    enable_stage1_whole_body_cbf: false,
    enable_stage1_self_collision_cbf: false,
    enable_stage1_velocity_limits: false,
    enable_stage1_force_limit_cbf: false,

    enable_stage2_cbf: true,
    enable_stage2_singularity_cbf: true,
    enable_stage2_ee_set_containment_cbf: false,
    enable_stage2_joint_limits_cbf: true,
    enable_stage2_collision_cbf: false,
    enable_stage2_whole_body_cbf: false,
    enable_stage2_self_collision_cbf: false,
    enable_stage2_velocity_limits: true,
    enable_stage2_acceleration_limits: true,
    enable_stage2_force_limit_cbf: false,

    use_effort_mode: false,
    use_commanded_pose_for_error: true,

    sim: false,
    override_sim: false,

    dt: 240.0,
    enable_debug_topics: false,
    debug_publish_rate: 5.0,
    enable_payload_estimation: false,
    print_tool_compensated_forces: false,
    print_execution_time: false,
  },
};
